import { PlusOutlined, WarningTwoTone } from '@ant-design/icons'
import { Image, Modal, Spin, Upload, UploadFile, UploadProps } from 'antd'
import { ItemRender } from 'antd/es/upload/interface'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { rolodexDeleteFile, useGetRolodexUploadUrl } from 'features/fde'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { IndicationDetailCardProps } from 'features/indicationTriage/useIndicationDetailCards'
import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import { UploadRequestOption } from 'rc-upload/lib/interface'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { RolodexFileRef, RolodexSignedURLResult } from 'types'

type RolodexFileQuery = NonNullable<Parameters<typeof useRolodexFileLS>['0']>

export interface ImageWallConfig {
  file_query: Omit<RolodexFileQuery, 'entry_id'>
  list_type: 'new_line' | 'wall'
}

const DefaultButtonContainer = styled.button`
  border: 0;
  background: none;
  cursor: pointer;
`

const DefaultButtonText = styled.div`
  margin-top: 8px;
`
// Styled component for the outer container
const NewLineButtonContainer = styled.div`
  margin-top: 5px;
  border: 1px dashed #ecf933;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: 16px;
  text-align: center;
  max-width: 250px;
  white-space: normal;
`

const NewLineButtonText = styled.div`
  margin-top: 8px;
`

const NEW_LINE_BUTTON_STYLE = {
  marginRight: 8,
  color: 'red',
  border: '2px solid #393939',
  borderColor: '#393939',
  borderRadius: '35px',
  backgroundColor: '#2b2b2b',
  padding: '8px 25px',
  cursor: 'pointer',
}

/**
 * Generate the path of a file, based on its name and the configured query.
 * The idea is that you don't upload something that doesn't get returned by
 * the query.
 *
 * @param name The name of the file
 * @param file_query the query
 * @returns the 'path' of the file within the entry
 */
function uploadPath(name: string, file_query: RolodexFileQuery): string {
  let path = ''

  if (file_query.path) {
    const pathParts = file_query.path.split('/')
    if (pathParts.length > 0) {
      const dirPart = pathParts[0]
      if (dirPart && !dirPart.includes('*') && !dirPart.includes('@')) {
        path = dirPart + '/'
      }
    }
  }

  path += name
  return path
}

const DEFAULT_FILE_QUERY: ImageWallConfig['file_query'] = {
  path: 'images/**',
}

export const Title = () => 'Images'
export const Width = '24rem'

/**
 * Hook to check which URLs are actually available, since there's a delay
 * after upload.
 *
 * Uses an expnential backoff to HEAD the signed URLs. A URL is considered
 * failed if we exceed the retry limit, or if a return code >= 500 is returned.
 *
 * @param signedUrls the URLs to check
 * @param retries number of retries to attempt
 * @param retryDelay the exponential backoff base
 * @returns 2 sets, [ available paths, error'd paths]
 */
function useCheckUploads(
  signedUrls: readonly RolodexSignedURLResult[],
  retries: number = 4,
  retryDelay: number = 500
): [ReadonlySet<string>, ReadonlySet<string>] {
  const { authorizedAxios: axios } = useGeckoAuth()
  const [uploadedPaths, setUploadedPaths] = useState<ReadonlySet<string>>(
    new Set()
  )
  const [errorPaths, setErrorPaths] = useState<ReadonlySet<string>>(new Set())
  const checkUploadPath = useCallback(
    async (path: string, retryCount: number) => {
      if (retryCount > retries) {
        setErrorPaths((prev) => new Set([...prev, path]))
        return
      }
      setTimeout(async () => {
        const urlResult = signedUrls.find((url) => url.path === path)
        if (!urlResult) {
          return
        }
        try {
          const headResp = await axios.head(urlResult.signed_url, {
            validateStatus: (status) => {
              return status < 500
            },
          })
          if (headResp.status === 200) {
            setUploadedPaths((prev) => new Set([...prev, urlResult.path]))
          } else {
            checkUploadPath(path, retryCount + 1)
          }
        } catch {
          setErrorPaths((prev) => new Set([...prev, path]))
        }
      }, retryCount * retryDelay)
    },
    [axios, retries, retryDelay, signedUrls]
  )
  useEffect(() => {
    setUploadedPaths(new Set())
    setErrorPaths(new Set())
    signedUrls.forEach(async (urlResult) => {
      checkUploadPath(urlResult.path, 0)
    })
  }, [checkUploadPath, signedUrls])

  return [uploadedPaths, errorPaths]
}

const ImagePreview: React.FC<{
  previewUrl: string | null
  setPreviewUrl: (url: string | null) => void
}> = ({ previewUrl, setPreviewUrl }) => {
  return (
    previewUrl && (
      <Image
        wrapperStyle={{ display: 'none' }}
        src={previewUrl}
        preview={{
          visible: !!previewUrl,
          onVisibleChange: (visible) => {
            if (!visible) {
              setPreviewUrl(null)
            }
          },
        }}
      />
    )
  )
}

function useImageList(
  fileRefs: readonly RolodexFileRef[],
  signedUrls: readonly RolodexSignedURLResult[],
  uploadedPaths: ReadonlySet<string>,
  errorPaths: ReadonlySet<string>
): UploadFile[] {
  return useMemo<UploadFile[]>(
    () =>
      fileRefs.map((fileRef) => {
        const url = signedUrls.find(
          (url) => url.path === fileRef.path
        )?.signed_url
        const parts = fileRef.path.split('/')
        const filename = parts[parts.length - 1]
        return {
          uid: `${fileRef.entry_id}_${fileRef.path}`,
          status: uploadedPaths.has(fileRef.path)
            ? 'done'
            : errorPaths.has(fileRef.path)
              ? 'error'
              : 'uploading',
          name: filename,
          url,
        }
      }),
    [errorPaths, fileRefs, signedUrls, uploadedPaths]
  )
}

interface GenericUploadProps {
  handlePreview: (file: UploadFile) => void
  imageList: readonly UploadFile[]
  action: UploadProps['action']
  deleteFile: (file: UploadFile) => void
  onChange: UploadProps['onChange']
  onRemove: UploadProps['onRemove']
  onPreview: UploadProps['onPreview']
  customRequest: UploadProps['customRequest']
}

const NewLineUpload: React.FC<GenericUploadProps> = ({
  handlePreview,
  imageList,
  action,
  onChange,
  onRemove,
  onPreview,
  customRequest,
  deleteFile,
}) => {
  const newLineItemRender = useCallback<ItemRender>(
    (originNode, file, fileList, actions: { remove: () => void }) => {
      const displayName = file.name
        .split('.')
        .slice(0, -1)
        .join('.')
        .split('_')
        .join(' ')

      return (
        <div key={file.uid} style={{ marginBottom: '16px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            {displayName}
          </div>

          {file.status === 'uploading' ? (
            <Spin />
          ) : file.status === 'error' || file.status === 'removed' ? (
            <WarningTwoTone />
          ) : (
            <img
              src={file.url}
              alt={file.name}
              style={{
                maxWidth: '100%',
                marginBottom: '8px',
                cursor: 'pointer',
              }}
              onClick={() => handlePreview(file)}
            />
          )}
          <div>
            <button
              onClick={() => handlePreview(file)}
              style={{ ...NEW_LINE_BUTTON_STYLE, color: 'yellow' }}>
              Preview
            </button>
            <button
              onClick={() => actions.remove()}
              style={NEW_LINE_BUTTON_STYLE}>
              Remove
            </button>
          </div>
        </div>
      )
    },
    [handlePreview]
  )

  const fileList = useMemo(() => [...imageList], [imageList])

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
      {imageList.map((file) =>
        newLineItemRender
          ? newLineItemRender(<></>, file, fileList, {
              remove: () => deleteFile(file),
              preview: () => {},
              download: () => {},
            })
          : null
      )}
      <Upload
        fileList={fileList}
        action={action}
        onChange={onChange}
        onRemove={onRemove}
        onPreview={onPreview}
        customRequest={customRequest}
        showUploadList={false}
        itemRender={newLineItemRender}>
        <NewLineButtonContainer>
          <PlusOutlined />
          <NewLineButtonText>
            Upload Image Supporting Concrete Defect Indication
          </NewLineButtonText>
        </NewLineButtonContainer>
      </Upload>
    </div>
  )
}

const ImageWallUpload: React.FC<GenericUploadProps> = ({
  imageList,
  action,
  onChange,
  onRemove,
  onPreview,
  customRequest,
}) => {
  const fileList = useMemo(() => [...imageList], [imageList])
  return (
    <Upload
      fileList={fileList}
      action={action}
      onChange={onChange}
      onRemove={onRemove}
      onPreview={onPreview}
      customRequest={customRequest}
      listType='picture-card'>
      <DefaultButtonContainer type='button'>
        <PlusOutlined />
        <DefaultButtonText>Upload</DefaultButtonText>
      </DefaultButtonContainer>
    </Upload>
  )
}

export const CardView: React.FC<IndicationDetailCardProps<ImageWallConfig>> = ({
  config,
}) => {
  const { indication: indicationItem } = useIndicationData()
  const { ROLODEX_URL } = useRuntimeVariables()
  const { authorizedAxios: axios } = useGeckoAuth()
  const fileQuery = useMemo(() => {
    return indicationItem
      ? {
          ...(config?.file_query ?? DEFAULT_FILE_QUERY),
          entry_id: indicationItem.id,
        }
      : undefined
  }, [config?.file_query, indicationItem])

  const { fileRefs, isLoading, forceRefresh } = useRolodexFileLS(fileQuery)
  const { results: signedUrls, isLoading: urlsLoading } = useRolodexSignedURLs(
    indicationItem
      ? fileRefs.map((ref) => ({
          entry_id: indicationItem.id,
          path: ref.path,
        }))
      : []
  )

  const [uploadedPaths, errorPaths] = useCheckUploads(signedUrls)

  // Translate the files into Ant.d format
  const imageList = useImageList(
    fileRefs,
    signedUrls,
    uploadedPaths,
    errorPaths
  )

  const deleteFile = useCallback(
    async (file: UploadFile) => {
      if (!fileQuery || !indicationItem) {
        return
      }
      const path = uploadPath(file.name, fileQuery)
      await rolodexDeleteFile(indicationItem.id, path, ROLODEX_URL, axios)
      forceRefresh()
    },
    [ROLODEX_URL, axios, fileQuery, forceRefresh, indicationItem]
  )

  const handlePreview = useCallback(
    (file: UploadFile) => {
      if (!fileQuery) {
        return
      }
      const path = uploadPath(file.name, fileQuery)
      const url = signedUrls.find((url) => url.path === path)?.signed_url
      setPreviewUrl(url ?? null)
    },
    [fileQuery, signedUrls]
  )

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [fileToDelete, setFileToDelete] = useState<UploadFile | null>(null)

  const getRolodexUploadUrl = useGetRolodexUploadUrl()

  const action = useCallback(
    async (file: UploadFile) => {
      if (!fileQuery || !indicationItem) {
        return ''
      }
      const uploadFilePath = uploadPath(file.name, fileQuery)
      return await getRolodexUploadUrl(indicationItem.id, uploadFilePath)
    },
    [fileQuery, getRolodexUploadUrl, indicationItem]
  )

  const customRequest: UploadProps['customRequest'] = useCallback(
    async (options: UploadRequestOption<unknown>) => {
      const { file, action } = options
      await axios.put(action as string, file as Blob, {
        headers: {
          'Content-Type': 'application/octet-stream',
        },
      })
      forceRefresh()
    },
    [axios, forceRefresh]
  )

  const doDelete = useCallback(() => {
    if (fileToDelete) {
      deleteFile(fileToDelete)
      setFileToDelete(null)
    }
  }, [deleteFile, fileToDelete])

  const uploadProps: GenericUploadProps = useMemo(
    () => ({
      action: !fileQuery || !indicationItem ? undefined : action,
      onChange: forceRefresh,
      imageList: imageList,
      onRemove: (file: UploadFile) => setFileToDelete(file),
      onPreview: handlePreview,
      customRequest: customRequest,
      handlePreview,
      deleteFile,
    }),
    [
      action,
      customRequest,
      deleteFile,
      fileQuery,
      forceRefresh,
      handlePreview,
      imageList,
      indicationItem,
    ]
  )
  return (
    <Spin spinning={urlsLoading || isLoading}>
      {config?.list_type === 'new_line' ? (
        <NewLineUpload {...uploadProps} />
      ) : (
        <ImageWallUpload {...uploadProps} />
      )}
      <ImagePreview previewUrl={previewUrl} setPreviewUrl={setPreviewUrl} />
      <Modal
        title='Confirm Delete'
        open={fileToDelete !== null}
        onOk={doDelete}
        onCancel={() => setFileToDelete(null)}
        okText='Delete'
        cancelText='Cancel'>
        <p>
          Are you sure you want to delete the file:{' '}
          <strong>{fileToDelete?.name}</strong>?
        </p>
      </Modal>
    </Spin>
  )
}
